import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Star, ChevronDown, TrendingUp, Award, Sun, Moon, Wrench, BookOpen, PhoneCall, LogOut, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { publicApi } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const HEADER_CACHE_KEY = 'wowrolla_header_nav_cache_v1';

const HARD_FALLBACK_CATEGORIES = [
	{ id: 'writing-assistants', name: 'Writing Assistants', slug: 'writing-assistants', description: '' },
	{ id: 'ai-agents', name: 'AI Agents', slug: 'ai-agents', description: '' },
	{ id: 'seo-tools', name: 'SEO Tools', slug: 'seo-tools', description: '' },
	{ id: 'image-generators', name: 'Image Generators', slug: 'image-generators', description: '' },
	{ id: 'chatbots-agents', name: 'Chatbots & Agents', slug: 'chatbots-agents', description: '' },
	{ id: 'code-assistants', name: 'Code Assistants', slug: 'code-assistants', description: '' },
];

const HARD_FALLBACK_TOOLS = [
	{ id: 'fallback-chatgpt', name: 'ChatGPT', slug: 'chatgpt', rating: { overall: 4.9 }, category_name: 'Writing Assistants' },
	{ id: 'fallback-claude', name: 'Claude', slug: 'claude', rating: { overall: 4.8 }, category_name: 'Writing Assistants' },
	{ id: 'fallback-midjourney', name: 'Midjourney', slug: 'midjourney', rating: { overall: 4.8 }, category_name: 'Image Generators' },
	{ id: 'fallback-runway', name: 'Runway', slug: 'runway', rating: { overall: 4.7 }, category_name: 'Image Generators' },
	{ id: 'fallback-make', name: 'Make', slug: 'make-integromat', rating: { overall: 4.7 }, category_name: 'AI Agents' },
	{ id: 'fallback-n8n', name: 'n8n', slug: 'n8n', rating: { overall: 4.8 }, category_name: 'AI Agents' },
];

const slugify = (value = '') =>
	String(value)
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

const buildCategoriesFromTools = (tools = [], limit = 100) => {
	const groups = new Map();
	tools.forEach((tool) => {
		const name = tool.category_name || 'AI Tools';
		const slug = tool.category_slug || slugify(name) || 'ai-tools';
		const id = tool.category_id || slug;
		if (!groups.has(id)) {
			groups.set(id, {
				id,
				name,
				slug,
				description: '',
				tools: [],
			});
		}
		groups.get(id).tools.push(tool);
	});

	return Array.from(groups.values())
		.sort((a, b) => b.tools.length - a.tools.length)
		.slice(0, limit)
		.map(({ id, name, slug, description }) => ({ id, name, slug, description }));
};

const getCategoryPreviewTools = (category, allTools = [], loadedTools = []) => {
	if (loadedTools.length) {
		return loadedTools.slice(0, 3);
	}

	const categoryId = String(category?.id || '').toLowerCase();
	const categorySlug = String(category?.slug || '').toLowerCase();
	const categoryName = String(category?.name || '').toLowerCase();

	const matched = allTools.filter((tool) => {
		const toolCategoryId = String(tool.category_id || '').toLowerCase();
		const toolCategorySlug = String(tool.category_slug || '').toLowerCase();
		const toolCategoryName = String(tool.category_name || '').toLowerCase();
		const normalizedToolSlug = slugify(toolCategoryName);

		return (
			(toolCategoryId && toolCategoryId === categoryId) ||
			(toolCategorySlug && toolCategorySlug === categorySlug) ||
			(toolCategoryName && toolCategoryName === categoryName) ||
			(normalizedToolSlug && normalizedToolSlug === categorySlug)
		);
	});

	return matched.slice(0, 3);
};

const Header = () => {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [userMenuOpen, setUserMenuOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
	const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
	const [activeDropdown, setActiveDropdown] = useState(null);
	const [scrolled, setScrolled] = useState(false);
	const [categories, setCategories] = useState([]);
	const [categoryTools, setCategoryTools] = useState({});
	const [allTools, setAllTools] = useState([]);
	const [topTools, setTopTools] = useState([]);
	const [topRated, setTopRated] = useState([]);
	const [siteBranding, setSiteBranding] = useState({});
	const [logoLoadFailed, setLogoLoadFailed] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const { theme, toggleTheme, getCategoryColor } = useTheme();
	const { isAuthenticated, user, logout } = useAuth();
	const lifecycleStatus = (!isAuthenticated || (user?.role || '').toLowerCase() !== 'user')
		? 'Guest'
		: (user?.newsletter_subscribed ? 'Committed User' : 'Client Login');

	const searchSuggestions = (() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q || q.length < 1) return [];

		return (allTools || [])
			.filter((tool) => {
				const name = String(tool.name || '').toLowerCase();
				const tagline = String(tool.tagline || '').toLowerCase();
				const category = String(tool.category_name || '').toLowerCase();
				return name.includes(q) || tagline.includes(q) || category.includes(q);
			})
			.slice(0, 7);
	})();

	useEffect(() => {
		loadData();
		const handleScroll = () => setScrolled(window.scrollY > 20);
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		const hydrateBranding = () => {
			try {
				const cached = JSON.parse(localStorage.getItem('wowrolla-site-settings') || '{}');
				setSiteBranding(cached || {});
			} catch {
				setSiteBranding({});
			}
		};

		const onThemeSettingsUpdated = (event) => {
			setSiteBranding(event?.detail || {});
			setLogoLoadFailed(false);
		};

		hydrateBranding();
		window.addEventListener('wowrolla-theme-settings-updated', onThemeSettingsUpdated);
		return () => window.removeEventListener('wowrolla-theme-settings-updated', onThemeSettingsUpdated);
	}, []);

	const currentThemeLogo = theme === 'light'
		? (siteBranding.logo_light_url || siteBranding.logo_url || '')
		: (siteBranding.logo_dark_url || siteBranding.logo_url || '');
	const shouldShowLogoImage = Boolean(currentThemeLogo) && !logoLoadFailed;

	useEffect(() => {
		setLogoLoadFailed(false);
	}, [currentThemeLogo, theme]);

		const loadData = async () => {
			try {
				let cached = null;
				try {
					cached = JSON.parse(localStorage.getItem(HEADER_CACHE_KEY) || 'null');
				} catch {
					cached = null;
				}

				const [catResult, topToolsResult, allToolsResult] = await Promise.allSettled([
					publicApi.getCategories(),
					publicApi.getTopToolsByClicks(5),
					publicApi.getAllTools(false),
				]);

				const allTools = allToolsResult.status === 'fulfilled'
					? (allToolsResult.value.data || [])
					: (cached?.allTools || []);

				const apiCategories = catResult.status === 'fulfilled'
					? (catResult.value.data || [])
					: [];
				const derivedCategories = buildCategoriesFromTools(allTools, 100);
				const categoryList = apiCategories.length
					? apiCategories
					: (derivedCategories.length
						? derivedCategories
						: (cached?.categories || HARD_FALLBACK_CATEGORIES));

				const apiTopTools = topToolsResult.status === 'fulfilled'
					? (topToolsResult.value.data || [])
					: [];
				const trendingTools = apiTopTools.length
					? apiTopTools
					: ((allTools || []).slice(0, 5).length ? (allTools || []).slice(0, 5) : (cached?.topTools || HARD_FALLBACK_TOOLS));

				setAllTools(allTools);
				setCategories(categoryList);
				setTopTools(trendingTools);
				// Top Picks = highest overall rating from featured tools
				const rated = allTools
					.filter((t) => t.rating?.overall)
					.sort((a, b) => (b.rating.overall || 0) - (a.rating.overall || 0))
					.slice(0, 4);
				setTopRated(rated.length ? rated : trendingTools.slice(0, 4));

				const toolsData = {};
				for (const cat of categoryList || []) {
					const fallbackTools = (allTools || [])
						.filter((tool) => {
							const toolCategoryId = tool.category_id || tool.category_slug;
							const toolCategoryName = (tool.category_name || '').toLowerCase();
							return toolCategoryId === cat.id || toolCategoryName === (cat.name || '').toLowerCase();
						})
						.slice(0, 3);

					const hardFallbackTools = HARD_FALLBACK_TOOLS
						.filter((tool) => (tool.category_name || '').toLowerCase() === (cat.name || '').toLowerCase())
						.slice(0, 3);

					toolsData[cat.id] = fallbackTools.length ? fallbackTools : hardFallbackTools;
				}
				setCategoryTools(toolsData);

				try {
					localStorage.setItem(
						HEADER_CACHE_KEY,
						JSON.stringify({
							categories: categoryList,
							topTools: trendingTools,
							allTools,
						})
					);
				} catch {
					// Ignore localStorage errors.
				}
			} catch (error) {
				console.error('Failed to load header data:', error);
				try {
					const cached = JSON.parse(localStorage.getItem(HEADER_CACHE_KEY) || 'null');
					if (cached) {
						setCategories(cached.categories || []);
						setAllTools(cached.allTools || HARD_FALLBACK_TOOLS);
						setTopTools(cached.topTools || []);
						setTopRated((cached.topTools || []).slice(0, 4));
					}
				} catch {
					// Ignore cache read errors.
				}
			}
		};

	const handleSearch = (e) => {
		e.preventDefault();
		if (searchQuery.trim()) {
			navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
			setSearchQuery('');
			setShowSearchSuggestions(false);
			setActiveSuggestionIndex(-1);
		}
	};

	const handleSuggestionSelect = (tool) => {
		if (!tool?.slug) return;
		navigate(`/tools/${tool.slug}`);
		setSearchQuery('');
		setShowSearchSuggestions(false);
		setActiveSuggestionIndex(-1);
		setMobileMenuOpen(false);
	};

	const handleSearchInputKeyDown = (e) => {
		if (!showSearchSuggestions || searchSuggestions.length === 0) return;

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActiveSuggestionIndex((prev) => (prev + 1) % searchSuggestions.length);
			return;
		}

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActiveSuggestionIndex((prev) => (prev <= 0 ? searchSuggestions.length - 1 : prev - 1));
			return;
		}

		if (e.key === 'Enter' && activeSuggestionIndex >= 0 && searchSuggestions[activeSuggestionIndex]) {
			e.preventDefault();
			handleSuggestionSelect(searchSuggestions[activeSuggestionIndex]);
			return;
		}

		if (e.key === 'Escape') {
			setShowSearchSuggestions(false);
			setActiveSuggestionIndex(-1);
		}
	};

	const menuItems = [
		{ label: 'Categories', hasDropdown: true,  dropdownType: 'categories', link: '/categories' },
		{ label: 'Pick Tools', hasDropdown: true,  dropdownType: 'tools', link: '/find' },
		{ label: 'Rankings',  hasDropdown: true,  dropdownType: 'rankings', link: '/best?sort=overall' },
		{ label: 'Resources',  hasDropdown: true,  dropdownType: 'resources' },
		{ label: 'Consulting', hasDropdown: true, dropdownType: 'consulting', link: '/consulting#start' },
	];

	return (
		<header
			className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
				scrolled ? 'glass-surface-strong shadow-lg' : 'glass-surface'
			}`}
			style={{ borderBottom: `1px solid var(--border-subtle)` }}
			data-testid="main-header"
		>
			<div className="container mx-auto px-6 py-4">
				<div className="flex items-center justify-between gap-3 min-w-0">
					{/* Logo */}
					<Link to="/" className="flex items-center space-x-2 flex-shrink-0" data-testid="logo-link">
						{shouldShowLogoImage ? (
							<img
								src={currentThemeLogo}
								alt="WOWROLLA"
								style={{ height: 'clamp(36px, 6vw, 64px)', width: 'auto', maxWidth: 'min(44vw, 320px)', objectFit: 'contain', display: 'block' }}
								onError={() => setLogoLoadFailed(true)}
							/>
						) : (
							<div className="text-2xl font-black" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--accent-primary)' }}>
								WOWROLLA
							</div>
						)}
					</Link>

					{/* Desktop Nav */}
					<div className="hidden xl:flex items-center justify-center space-x-1 flex-1 min-w-0 ml-4" data-testid="desktop-nav">
						{menuItems.map((item, index) => (
							<div
								key={index}
								className="relative"
								onMouseEnter={() => item.hasDropdown && setActiveDropdown(item.label)}
								onMouseLeave={() => setActiveDropdown(null)}
							>
								{/* Consulting highlighted pill */}
								{item.highlight ? (
									<Link
										to={item.link}
										className="ml-2 px-4 py-1.5 text-sm font-bold rounded-full transition-all hover:scale-105 inline-flex items-center gap-1.5 whitespace-nowrap"
										style={{ border: '1px solid rgba(var(--glow-1-rgb),0.65)', color: 'var(--glow-1)', background: 'rgba(var(--glow-1-rgb),0.08)', boxShadow: '0 0 14px rgba(var(--glow-1-rgb),0.18)' }}
										data-testid="nav-consulting-cta"
									>
										<PhoneCall size={13} />
										{item.label}
									</Link>
								) : item.link && !item.hasDropdown ? (
									<Link
										to={item.link}
										className="px-4 py-2 text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap"
										onMouseEnter={(e) => {

										}}
										onMouseLeave={(e) => {

										}}
										style={{ color: 'var(--text-primary)' }}
									>
										{item.label}
									</Link>
								) : item.link && item.hasDropdown ? (
									<Link to={item.link} className="px-3 py-2 text-[13px] font-medium flex items-center gap-1 transition-colors hover:opacity-80 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
										{item.label}
										<ChevronDown size={14} />
									</Link>
								) : (
									<button className="px-3 py-2 text-[13px] font-medium flex items-center gap-1 transition-colors hover:opacity-80 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
										{item.label}
										{item.hasDropdown && <ChevronDown size={14} />}
									</button>
								)}

								{/* Categories Mega Dropdown */}
								{item.dropdownType === 'categories' && (
									<AnimatePresence>
										{activeDropdown === item.label && (
											<motion.div
												initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
												className="absolute top-full left-0 mt-2 w-[800px]"
											>
												<div className="mega-menu-glass rounded-xl p-6 shadow-2xl max-h-[70vh] overflow-y-auto">
													<div className="grid grid-cols-2 gap-6">
														{categories.length === 0 && (
															<div className="col-span-2 rounded-lg p-3" style={{ border: '1px solid var(--border-subtle)' }}>
																<p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
																	Categories are temporarily loading. You can still browse everything.
																</p>
																<div className="flex items-center gap-4 text-sm">
																	<Link to="/categories" className="hover:underline" style={{ color: 'var(--accent-primary)' }}>View all categories</Link>
																	<Link to="/find" className="hover:underline" style={{ color: 'var(--accent-secondary)' }}>Use AI finder</Link>
																</div>
															</div>
														)}
														{categories.map((category, catIdx) => {
															const color = getCategoryColor(catIdx, categories.length);
															const previewTools = getCategoryPreviewTools(category, allTools, categoryTools[category.id] || []);
															return (
																<div key={category.id} className="space-y-3">
																	<Link to={`/categories/${category.slug}`} className="block font-semibold hover:underline" style={{ color: color.accent }}>
																		{category.name} &rarr;
																	</Link>
																	<div className="space-y-2">
																		{previewTools.map((tool) => (
																			<Link key={tool.id} to={`/tools/${tool.slug}`}
																				className="block p-2 rounded transition-colors"
																				onMouseEnter={e => e.currentTarget.style.background = color.bg}
																				onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
																			>
																				<div className="flex items-center justify-between">
																					<span className="text-sm" style={{ color: 'var(--text-primary)' }}>{tool.name}</span>
																					{tool.rating && (
																						<div className="flex items-center gap-1">
																							<Star size={12} style={{ color: color.accent, fill: color.accent }} />
																							<span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{tool.rating?.overall || 'N/A'}</span>
																						</div>
																					)}
																				</div>
																			</Link>
																		))}
																		{previewTools.length === 0 && (
																			<p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Tools loading for this category.</p>
																		)}
																	</div>
																</div>
															);
														})}
													</div>
													<div className="mt-4 pt-4 text-center" style={{ borderTop: `1px solid var(--border-subtle)` }}>
														<Link to="/categories" className="text-sm hover:underline" style={{ color: 'var(--accent-primary)' }}>
															View All Categories &rarr;
														</Link>
													</div>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								)}

								{/* Tools Dropdown */}
								{item.dropdownType === 'tools' && (
									<AnimatePresence>
										{activeDropdown === item.label && (
											<motion.div
												initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
												className="absolute top-full left-0 mt-2 w-[260px]"
											>
												<div className="mega-menu-glass rounded-xl p-5 shadow-2xl">
													<div className="flex items-center gap-2 mb-4">
														<Wrench size={15} style={{ color: 'var(--accent-primary)' }} />
														<h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Discover &amp; Track</h3>
													</div>
													<div className="space-y-1">
														{[
															{ label: '🔍 Find the Right Tool', link: '/find' },
															{ label: '🧠 AI Stacks', link: '/ai-stacks' },
															{ label: '⚖️ Compare Side-by-Side', link: '/compare' },
															{ label: '🧩 Build My Stack', link: '/my-stack/build' },
															{ label: '📊 Stack Dashboard', link: '/dashboard/stack' },
														].map(({ label, link }) => (
															<Link key={link} to={link}
																className="flex items-center px-3 py-2.5 text-sm rounded-lg transition-colors"
																style={{ color: 'var(--text-secondary)' }}
																onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
																onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
															>
																{label}
															</Link>
														))}
													</div>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								)}

							{item.dropdownType === 'rankings' && (
								<AnimatePresence>
									{activeDropdown === item.label && (
										<motion.div
											initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
											className="absolute top-full left-0 mt-2 w-[520px]"
										>
											<div className="mega-menu-glass rounded-xl p-6 shadow-2xl">
												<div className="flex items-center gap-2 mb-1">
													<Award size={16} style={{ color: 'var(--glow-1)' }} />
													<h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Rankings Hub</h3>
												</div>
												<p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Use cases on the left, sort criteria on top, and both click leaders and editor picks in one place.</p>
												<div className="grid grid-cols-2 gap-5">
													<div>
														<div className="flex items-center gap-2 mb-3">
															<TrendingUp size={14} style={{ color: '#f97316' }} />
															<div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: '#f97316' }}>Trending</div>
														</div>
														<div className="space-y-1">
															{topTools.slice(0, 4).map((tool, i) => (
																<Link key={tool.id} to={`/tools/${tool.slug}`}
																	className="flex items-center gap-3 p-2.5 rounded-lg transition-colors"
																	onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
																	onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
																>
																	<span className="text-xs font-bold w-5 text-center rounded" style={{ color: '#f97316', background: 'rgba(249,115,22,0.12)' }}>#{i + 1}</span>
																	<div className="flex-1 min-w-0">
																		<div className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{tool.name}</div>
																		<div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{tool.tagline || tool.category_name}</div>
																	</div>
																</Link>
															))}
														</div>
													</div>
													<div>
														<div className="flex items-center gap-2 mb-3">
															<Star size={14} style={{ color: 'var(--glow-1)' }} />
															<div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--glow-1)' }}>Top Rated</div>
														</div>
														<div className="space-y-1">
															{topRated.slice(0, 4).map((tool, i) => (
																<Link key={tool.id} to={`/tools/${tool.slug}`}
																	className="flex items-center justify-between p-2.5 rounded-lg transition-colors"
																	onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
																	onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
																>
																	<div className="min-w-0">
																		<div className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>#{i + 1} {tool.name}</div>
																		<div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{tool.category_name || tool.tagline}</div>
																	</div>
																	<div className="flex items-center gap-1 text-xs flex-shrink-0" style={{ color: 'var(--glow-1)' }}>
																		<Star size={11} style={{ fill: 'currentColor' }} />{tool.rating?.overall || '—'}
																	</div>
																</Link>
															))}
														</div>
													</div>
												</div>
												<div className="mt-4 pt-4 text-center" style={{ borderTop: `1px solid var(--border-subtle)` }}>
													<Link to="/best?sort=overall" className="text-sm hover:underline inline-flex items-center gap-1" style={{ color: 'var(--glow-1)' }}>
														<Award size={14} /> Open Rankings Console &rarr;
													</Link>
												</div>
											</div>
											</motion.div>
										)}
									</AnimatePresence>
								)}

								{/* Resources Dropdown */}
								{item.dropdownType === 'resources' && (
									<AnimatePresence>
										{activeDropdown === item.label && (
											<motion.div
												initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
												className="absolute top-full left-0 mt-2 w-[240px]"
											>
												<div className="mega-menu-glass rounded-xl p-5 shadow-2xl">
													<div className="flex items-center gap-2 mb-4">
														<BookOpen size={15} style={{ color: 'var(--accent-primary)' }} />
														<h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Learn &amp; Connect</h3>
													</div>
													<div className="space-y-1">
														{[
															{ label: '📧 Newsletter', link: '/newsletter' },
															{ label: '📘 Guides', link: '/guides' },
															{ label: '📝 Blog', link: '/blog' },
															{ label: '❓ FAQ', link: '/faq' },
															{ label: '⭐ Reviews', link: '/reviews' },
															{ label: '🔍 Transparency', link: '/transparency' },
														].map(({ label, link }) => (
															<Link key={link} to={link}
																className="flex items-center px-3 py-2.5 text-sm rounded-lg transition-colors"
																style={{ color: 'var(--text-secondary)' }}
																onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
																onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
															>
																{label}
															</Link>
														))}
													</div>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								)}

								{/* Consulting Dropdown */}
								{item.dropdownType === 'consulting' && (
									<AnimatePresence>
										{activeDropdown === item.label && (
											<motion.div
												initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
												className="absolute top-full left-0 mt-2 w-[280px]"
											>
												<div className="mega-menu-glass rounded-xl p-5 shadow-2xl">
													<div className="flex items-center gap-2 mb-4">
														<PhoneCall size={15} style={{ color: 'var(--glow-1)' }} />
														<h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Consulting</h3>
													</div>
													<div className="space-y-1">
														{[
															{ label: '📞 Book a Discovery Call', link: '/consulting#start' },
															{ label: '🧪 My AI Flow', link: '/consulting#my-flow' },
															{ label: '📋 AI Readiness Studio', link: '/consulting#roi' },
															{ label: '📦 Packages and Pricing', link: '/consulting#packages' },
														].map(({ label, link }) => (
															<Link key={link} to={link}
																className="flex items-center px-3 py-2.5 text-sm rounded-lg transition-colors"
																style={{ color: 'var(--text-secondary)' }}
																onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
																onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
															>
																{label}
															</Link>
														))}
													</div>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								)}
							</div>
						))}
					</div>

					{/* Search + Theme + Admin */}
					<div className="hidden xl:flex items-center space-x-2 flex-shrink-0">
						<span
							className="hidden 2xl:inline-flex items-center font-mono text-[10px] uppercase tracking-widest px-3 py-2"
							style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
						>
							{lifecycleStatus}
						</span>
						<form onSubmit={handleSearch} className="relative hidden 2xl:block">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={16} style={{ color: 'var(--text-muted)' }} />
							<input
								type="text" value={searchQuery}
								onChange={(e) => {
									setSearchQuery(e.target.value);
									setShowSearchSuggestions(Boolean(e.target.value.trim()));
									setActiveSuggestionIndex(-1);
								}}
								onFocus={() => {
									if (searchQuery.trim()) {
										setShowSearchSuggestions(true);
									}
								}}
								onBlur={() => {
									setTimeout(() => {
										setShowSearchSuggestions(false);
										setActiveSuggestionIndex(-1);
									}, 120);
								}}
								onKeyDown={handleSearchInputKeyDown}
								placeholder="Search tools..."
								className="pl-10 pr-4 py-2 text-sm w-44 rounded-full transition-all"
								style={{ background: 'transparent', border: `1px solid var(--border-strong)`, color: 'var(--text-primary)' }}
							/>
							{showSearchSuggestions && searchSuggestions.length > 0 && (
								<div
									className="absolute top-full left-0 mt-2 w-80 rounded-xl overflow-hidden z-50"
									style={{ background: 'var(--bg-elevated)', border: `1px solid var(--border-strong)` }}
								>
									{searchSuggestions.map((tool, index) => (
										<button
											key={tool.id || tool.slug || `${tool.name}-${index}`}
											type="button"
											onMouseDown={() => handleSuggestionSelect(tool)}
											className="w-full text-left px-3 py-2.5 border-b last:border-b-0"
											style={{
												borderColor: 'var(--border-subtle)',
												background: activeSuggestionIndex === index ? 'var(--bg-layer)' : 'transparent',
											}}
										>
											<p className="text-sm" style={{ color: 'var(--text-primary)' }}>{tool.name}</p>
											<p className="text-xs" style={{ color: 'var(--text-muted)' }}>{tool.category_name || tool.tagline || 'AI Tool'}</p>
										</button>
									))}
									<button
										type="submit"
										className="w-full text-left px-3 py-2.5 text-xs"
										style={{ color: 'var(--accent-primary)', background: 'var(--bg-layer)' }}
									>
									</button>
								</div>
							)}
						</form>
						{isAuthenticated && (user?.role || '').toLowerCase() === 'user' ? (
							<div className="relative">
								<button
									type="button"
									onClick={() => setUserMenuOpen((p) => !p)}
									className="hidden xl:flex items-center gap-2 px-3 py-1.5 transition-colors"
									style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)' }}
								>
									<div
										className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
										style={{ background: 'var(--glow-1)', color: 'var(--bg-base)' }}
									>
										{(user?.username || user?.email || 'U')[0].toUpperCase()}
									</div>
									<span className="font-mono text-[10px] max-w-[96px] truncate" style={{ color: 'var(--text-primary)' }}>
										{user?.username || user?.email?.split('@')[0] || 'Account'}
									</span>
									<ChevronDown size={11} style={{ color: 'var(--text-muted)' }} />
								</button>
								{userMenuOpen && (
									<div
										className="absolute right-0 top-full mt-1 w-52 z-50 shadow-xl"
										style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)' }}
									>
										<div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
											<p className="text-xs font-bold truncate" style={{ color: 'var(--text-primary)' }}>{user?.username || 'User'}</p>
											<p className="text-[10px] truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
											<p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Readiness Studio first, then My AI Flow</p>
										</div>
										<Link
											to="/dashboard/stack"
											onClick={() => setUserMenuOpen(false)}
											className="flex items-center gap-2 px-4 py-2.5 text-sm transition-colors"
											style={{ color: 'var(--text-primary)' }}
											onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-layer)'; }}
											onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
										>
											<User size={13} /> My Stack
										</Link>
										<button
											type="button"
											onClick={() => {
												const ready = window.localStorage.getItem('haiy_readiness_completed') === '1';
												navigate(ready ? '/my-ai-flow' : '/consulting#roi');
												setUserMenuOpen(false);
											}}
											className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors text-left"
											style={{ color: 'var(--text-primary)' }}
											onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-layer)'; }}
											onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
										>
											<BookOpen size={13} /> My AI Flow
										</button>
										<div className="border-t" style={{ borderColor: 'var(--border-subtle)' }} />
										<button
											type="button"
											onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }}
											className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors text-left"
											style={{ color: 'var(--glow-4)' }}
											onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-layer)'; }}
											onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
										>
											<LogOut size={13} /> Sign Out
										</button>
									</div>
								)}
							</div>
						) : (
							<Link
								to="/login"
								className="hidden xl:inline-flex items-center btn-secondary font-mono text-[10px] uppercase tracking-widest px-4 py-2 whitespace-nowrap"
							>
								Client Login
							</Link>
						)}
						<Link
							to="/consulting#start"
							className="hidden 2xl:inline-flex items-center btn-primary font-mono text-[10px] uppercase tracking-widest px-4 py-2 whitespace-nowrap"
						>
							Free Discovery Call
						</Link>
						<button
							onClick={toggleTheme}
							className="p-2 rounded-full transition-all hover:scale-110"
							style={{ background: 'var(--bg-elevated)', border: `1px solid var(--border-subtle)` }}
							data-testid="theme-toggle"
							title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
						>
							{theme === 'dark' ? <Sun size={18} style={{ color: 'var(--accent-primary)' }} /> : <Moon size={18} style={{ color: 'var(--accent-primary)' }} />}
						</button>
						<Link to="/admin" className="btn-secondary text-sm" data-testid="nav-admin">Admin</Link>
					</div>

					{/* Mobile toggle */}
					<div className="xl:hidden flex items-center gap-2">
						<button onClick={toggleTheme} className="p-2 rounded-full" style={{ background: 'var(--bg-elevated)' }} data-testid="theme-toggle-mobile">
							{theme === 'dark' ? <Sun size={18} style={{ color: 'var(--accent-primary)' }} /> : <Moon size={18} style={{ color: 'var(--accent-primary)' }} />}
						</button>
						<button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button" style={{ color: 'var(--text-primary)' }}>
							{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
						</button>
					</div>
				</div>

				{/* Mobile Menu */}
				{mobileMenuOpen && (
					<div className="xl:hidden mt-4 pb-4" data-testid="mobile-menu">
						<form onSubmit={handleSearch} className="mb-4">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={16} style={{ color: 'var(--text-muted)' }} />
								<input type="text" value={searchQuery}
									onChange={(e) => {
										setSearchQuery(e.target.value);
										setShowSearchSuggestions(Boolean(e.target.value.trim()));
										setActiveSuggestionIndex(-1);
									}}
									onFocus={() => {
										if (searchQuery.trim()) {
											setShowSearchSuggestions(true);
										}
									}}
									onBlur={() => {
										setTimeout(() => {
											setShowSearchSuggestions(false);
											setActiveSuggestionIndex(-1);
										}, 120);
									}}
									onKeyDown={handleSearchInputKeyDown}
									placeholder="Search tools..."
									className="w-full pl-10 pr-4 py-2 text-sm rounded-full"
									style={{ background: 'transparent', border: `1px solid var(--border-strong)`, color: 'var(--text-primary)' }}
								/>
								{showSearchSuggestions && searchSuggestions.length > 0 && (
									<div
										className="absolute top-full left-0 mt-2 w-full rounded-xl overflow-hidden z-50"
										style={{ background: 'var(--bg-elevated)', border: `1px solid var(--border-strong)` }}
									>
										{searchSuggestions.map((tool, index) => (
											<button
												key={`mobile-${tool.id || tool.slug || `${tool.name}-${index}`}`}
												type="button"
												onMouseDown={() => handleSuggestionSelect(tool)}
												className="w-full text-left px-3 py-2.5 border-b last:border-b-0"
												style={{
													borderColor: 'var(--border-subtle)',
													background: activeSuggestionIndex === index ? 'var(--bg-layer)' : 'transparent',
												}}
											>
												<p className="text-sm" style={{ color: 'var(--text-primary)' }}>{tool.name}</p>
												<p className="text-xs" style={{ color: 'var(--text-muted)' }}>{tool.category_name || tool.tagline || 'AI Tool'}</p>
											</button>
										))}
										<button
											type="submit"
											className="w-full text-left px-3 py-2.5 text-xs"
											style={{ color: 'var(--accent-primary)', background: 'var(--bg-layer)' }}
										>
											Search all results for "{searchQuery.trim()}" ->
										</button>
									</div>
								)}
							</div>
						</form>
						<nav className="flex flex-col space-y-1">
							<Link to="/categories" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📂 Categories</Link>
							<Link to="/find" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>🧰 Pick Tools</Link>
							<Link to="/ai-stacks" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>🧠 AI Stacks</Link>
							<Link to="/compare" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>⚖️ Compare</Link>
							<Link to="/my-stack/build" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>🧩 My Stack</Link>
							{isAuthenticated && (user?.role || '').toLowerCase() === 'user' && (
								<Link
									to={(typeof window !== 'undefined' && window.localStorage.getItem('haiy_readiness_completed') === '1') ? '/my-ai-flow' : '/consulting#roi'}
									className="py-2 px-2 text-sm"
									style={{ color: 'var(--text-primary)' }}
									onClick={() => setMobileMenuOpen(false)}
								>
									🧪 My AI Flow
								</Link>
							)}
							<Link to="/dashboard/stack" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📊 Stack Dashboard</Link>
							<Link to="/best?sort=overall" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>🏆 Rankings</Link>
							<Link to="/newsletter" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📧 Newsletter</Link>
							<Link to="/guides" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📘 Guides</Link>
							<Link to="/blog" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📝 Blog</Link>
							<Link to="/faq" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>❓ FAQ</Link>
							<Link to="/reviews" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>⭐ Reviews</Link>
							<Link to="/consulting#start" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📞 Book a Discovery Call</Link>
							<Link to="/consulting#roi" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📋 AI Readiness Studio</Link>
							<Link to="/consulting#packages" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>📦 Packages and Pricing</Link>
							<p className="py-2 px-2 text-xs uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
								Status: {lifecycleStatus}
							</p>
							<Link to="/transparency" className="py-2 px-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>🔍 Transparency</Link>
							<Link to="/consulting"
								className="py-2.5 px-4 mt-3 text-sm font-bold rounded-full text-center"
								style={{ border: '1px solid rgba(var(--glow-1-rgb),0.65)', color: 'var(--glow-1)', background: 'rgba(var(--glow-1-rgb),0.08)' }}
								onClick={() => setMobileMenuOpen(false)}
							>
								📞 Book Consulting
							</Link>
							<Link to={(isAuthenticated && (user?.role || '').toLowerCase() === 'user') ? '/dashboard/stack' : '/login'} className="py-2 px-2 mt-2 text-sm" style={{ color: 'var(--text-primary)' }} onClick={() => setMobileMenuOpen(false)}>
								{(isAuthenticated && (user?.role || '').toLowerCase() === 'user') ? 'Client Dashboard' : 'Client Login'}
							</Link>
							{isAuthenticated && (
								<button
									type="button"
									onClick={() => { logout(); setMobileMenuOpen(false); navigate('/'); }}
									className="py-2 px-2 mt-1 text-sm text-left flex items-center gap-2"
									style={{ color: 'var(--glow-4)' }}
								>
									<LogOut size={13} /> Sign Out
								</button>
							)}							<Link to="/admin" className="py-2 px-2 mt-2 text-sm" style={{ color: 'var(--accent-primary)' }} onClick={() => setMobileMenuOpen(false)}>Admin</Link>
						</nav>
					</div>
				)}
			</div>
		</header>
	);
};

export default Header;


